import { put, takeLatest, call } from "redux-saga/effects";
import * as actionTypes from "./actionTypes";
import * as actions from "./actions";
import { db } from "helpers/firebase/firebaseConfig";
import { 
  collection, getDocs,getDoc, doc, deleteDoc, updateDoc, addDoc,
  query, where, writeBatch
} from "firebase/firestore";
import { v4 as uuidv4 } from 'uuid';
import generateRandomPassword from "store/serviceProviders/generatedPass";
import sms from "helpers/api/sms";
import sendEmail from "helpers/api/resend";

// Collection names
const SERVICE_PROVIDERS = "service_providers";
const JOIN_REQUESTS = "service_provider_join_requests";
const BOOKINGS = "Booking";
const PROVIDER_SERVICES = "service_provider_services";
const PROVIDER_NOTIFICATIONS = "serviceProvidersNotifications";
const DELETION_LOGS = "services_deletion_logs";


const addServiceProviderApi = async (newServiceProvider) => {
  // Implement if needed
};

const removeServiceProviderApi = async (serviceProviderId) => {
  const batch = writeBatch(db);
  const deletionBatch = writeBatch(db); // Separate batch for logging

  try {
    // 1. First get the provider document for logging
    const providerRef = doc(db, SERVICE_PROVIDERS, serviceProviderId);
    const providerDoc = await getDoc(providerRef);
    
    if (!providerDoc.exists()) {
      throw new Error("Service provider not found");
    }

    // 2. Prepare deletion log data
    const deletionData = {
      provider_id: serviceProviderId,
      provider_data: providerDoc.data(),
      deleted_at: new Date().toISOString(),
      deleted_by: "admin", // Replace with actual user ID if available
      related_services: [],
      related_bookings: [],
      related_invoices: [],
      related_notifications: []
    };

    // 3. Get and log all services
    const servicesQuery = query(
      collection(db, PROVIDER_SERVICES),
      where("service_provider_id", "==", serviceProviderId)
    );
    const servicesSnapshot = await getDocs(servicesQuery);
    servicesSnapshot.forEach((serviceDoc) => {
      deletionData.related_services.push({
        id: serviceDoc.id,
        data: serviceDoc.data()
      });
      batch.delete(serviceDoc.ref);
    });

    // 4. Get and log all bookings
    const bookingsQuery = query(
      collection(db, BOOKINGS),
      where("service_providers_id", "==", serviceProviderId)
    );
    const bookingsSnapshot = await getDocs(bookingsQuery);
    bookingsSnapshot.forEach((bookingDoc) => {
      deletionData.related_bookings.push({
        id: bookingDoc.id,
        data: bookingDoc.data()
      });
      batch.delete(bookingDoc.ref);
    });

    // 5. Get and log all notifications
    const notificationsQuery = query(
      collection(db, PROVIDER_NOTIFICATIONS),
      where("serviceProviders", "==", serviceProviderId)
    );
    const notificationsSnapshot = await getDocs(notificationsQuery);
    notificationsSnapshot.forEach((notificationDoc) => {
      deletionData.related_notifications.push({
        id: notificationDoc.id,
        data: notificationDoc.data()
      });
      batch.delete(notificationDoc.ref);
    });

    // 6. Get and log all invoices
    const invoicesQuery = query(
      collection(db, "service_provider_sale_invoices"),
      where("serviceProviders", "==", serviceProviderId)
    );
    const invoicesSnapshot = await getDocs(invoicesQuery);
    invoicesSnapshot.forEach((invoiceDoc) => {
      deletionData.related_invoices.push({
        id: invoiceDoc.id,
        data: invoiceDoc.data()
      });
      batch.delete(invoiceDoc.ref);
    });

    // 7. Create deletion log document first
    const logRef = doc(collection(db, DELETION_LOGS));
    deletionBatch.set(logRef, deletionData);
    await deletionBatch.commit();

    // 8. Delete the provider (must be last to maintain references)
    batch.delete(providerRef);

    // 9. Commit all deletions
    await batch.commit();

    return { success: true, deletedCount: {
      services: servicesSnapshot.size,
      bookings: bookingsSnapshot.size,
      invoices: invoicesSnapshot.size,
      notifications: notificationsSnapshot.size
    }};

  } catch (error) {
    console.error("Complete deletion failed:", error);
    throw new Error(`Failed to delete service provider and all related data: ${error.message}`);
  }
};

const approveServiceProviderApi = async (serviceProviderId) => {

  const { email, full_name, mobile, commercial_register, tax_number, salon_name } = serviceProviderId;
  const generatedPass = generateRandomPassword(8);
  
  try {
    const provider = {
      full_name: full_name,
      salon_name: salon_name,
      mobile: mobile,
      email: email,
      commercial_register: commercial_register,
      tax_number: tax_number,
      account_status: "active",
      password: generatedPass,
      description: "للعناية بالجمال والمظهر الشخصي",
      filed: true,
      img_id: '',
      is_business: true,
      is_fovarite: false,
      is_online: false,
      location_longitude: 0,
      location_latitude: 0,
      address: '',
      account_name: '',
      bank_account: '',
      profile_img_url: '',
      notification_token: [],
      bank_account: '',
      sub_date: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      erp_account: {}, 
      commission: 1,
      order_cancellation_count: 0,
      entity_unified_no: 'n/a'
    };
    
    const docRef = await addDoc(collection(db, SERVICE_PROVIDERS), provider);
        // Create the initial revenue record
    await addDoc(collection(db, "total_revenue"), {
     service_provider_id: docRef.id,
      amount: 0,
      last_updated: new Date().toISOString(),
    });

  
    // sms({ ...provider, id: docRef.id }); // Send SMS with login credentials
    sendEmail({ ...provider, id: docRef.id }); // Send email with login credentials
    
    return { id: docRef.id, ...provider };
  } catch (error) {
    throw error;
  }
};

const rejectServiceProviderApi = async (collId, doId) => {
  await deleteDoc(doc(db, JOIN_REQUESTS, doId));
};

const fetchServiceProvidersApi = async () => {
  const querySnapshot = await getDocs(collection(db, SERVICE_PROVIDERS));
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

const fetchServiceProviderRequestsApi = async () => {
  const querySnapshot = await getDocs(collection(db, JOIN_REQUESTS));
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

const filterServiceProviderRequests = (registeredProviders, requests) => {
  const registeredEmails = new Set(registeredProviders.map(provider => provider.email));
  const registeredMobiles = new Set(registeredProviders.map(provider => provider.mobile));

  return requests.filter(request => {
    const isRegistered = registeredEmails.has(request.email) || registeredMobiles.has(request.mobile);
    if (isRegistered) {
      removeServiceProviderRequest(request.id);
    }
    return !isRegistered;
  });
};

const removeServiceProviderRequest = async (requestId) => {
  try {
    await deleteDoc(doc(db, JOIN_REQUESTS, requestId));
  } catch (error) {
    console.error('Error removing service provider request:', error);
  }
};





const UpdateStatus = async (accountId, newStatus) => {
  await updateDoc(doc(db, SERVICE_PROVIDERS, accountId), {
    account_status: newStatus
  });
};

const updateUserApi = async (userData) => {
  try {
    const keyMapping = {
      full_name: 'full_name',
      location: 'address',
      bank_account: 'bank_account',
      account_name: 'account_name',
      description: 'description',
      email: 'email',
      password: 'password',
      mobile: 'mobile',
      salon_name: 'salon_name',
      commission: 'commission',
      order_cancellation_count: 'order_cancellation_count',
      entity_unified_no: 'entity_unified_no',
      commercial_register: 'commercial_register',
      tax_number: 'tax_number',
      enable_tmara: "enable_tmara", // New field

    };

    const updatedFormData = {};


for (const key in keyMapping) {
  if (userData[key] !== null && userData[key] !== undefined) {
    if (key === "enable_tmara") {
      updatedFormData[keyMapping[key]] = userData[key] === true;

    } else if (key === "commission") {
      const commissionValue = parseFloat(userData[key]);

      if (
        isNaN(commissionValue) ||
        commissionValue < 0 ||
        commissionValue > 100
      ) {
        throw new Error(
          "Commission must be a number between 0 and 100"
        );
      }

      updatedFormData[keyMapping[key]] = commissionValue;

    } else if (key === "order_cancellation_count") {
      const countValue = Number(userData[key]);

      if (!Number.isInteger(countValue) || countValue < 0) {
        throw new Error(
          "Order cancellation count must be a non-negative integer"
        );
      }

      updatedFormData[keyMapping[key]] = countValue;

    } else {
      updatedFormData[keyMapping[key]] = userData[key];
    }
  }
}


    await updateDoc(doc(db, SERVICE_PROVIDERS, userData.id), updatedFormData);
  } catch (error) {
    throw error;
  }
};

function* fetchServiceProviders() {
  try {
    const serviceProviders = yield call(fetchServiceProvidersApi);
    yield put(actions.fetchServiceProvidersSuccess(serviceProviders));
  } catch (error) {
    yield put(actions.fetchServiceProvidersFailure(error));
  }
}

function* addServiceProvider(action) {
  try {
    const newServiceProvider = yield call(
      addServiceProviderApi,
      action.newServiceProvider
    );
    yield put(actions.addServiceProviderSuccess(newServiceProvider));
  } catch (error) {
    yield put(actions.addServiceProviderFailure(error));
  }
}

function* fetchServiceProviderRequests() {
  try {
    const registeredProviders = yield call(fetchServiceProvidersApi);
    const requests = yield call(fetchServiceProviderRequestsApi);
    
    const filteredRequests = filterServiceProviderRequests(registeredProviders, requests);
    
    yield put(actions.fetchServiceProviderRequestsSuccess(filteredRequests));
  } catch (error) {
    yield put(actions.fetchServiceProviderRequestsFailure(error));
  }
}

function* removeServiceProvider(action) {
  try {
    yield call(removeServiceProviderApi, action.serviceProviderId);
    yield put(actions.removeServiceProviderSuccess(action.serviceProviderId));
    
    // Refresh all relevant data
    yield put(actions.fetchServiceProvidersRequest());
    yield put(actions.fetchServiceProviderRequestsRequest());
    
  } catch (error) {
    yield put(actions.removeServiceProviderFailure(error));
    console.error("Failed to delete service provider:", error);
  }
}

function* approveServiceProvider(action) {
  try {
    const { id, collectionId } = action.serviceProviderId;
    yield call(approveServiceProviderApi, action.serviceProviderId);
    yield put(actions.approveServiceProviderSuccess(action.serviceProviderId));
    yield put(actions.rejectServiceProviderRequest({ collectionId, id }));
    yield put(actions.fetchServiceProvidersRequest());
  } catch (error) {
    yield put(actions.approveServiceProviderFailure(error.message));
  }
}

function* rejectServiceProvider(action) {
  try {
    const { collectionId, id } = action.serviceProviderId;
    yield call(rejectServiceProviderApi, collectionId, id);
    yield call(action.fetchServiceProviderRequestsRequest());
    yield put(actions.rejectServiceProviderSuccess(id));
  } catch (error) {
    yield put(actions.rejectServiceProviderFailure(error));
  }
}

function* updateAccountStatus(action) {
  try {
    const { accountId, newStatus } = action.payload;
    yield call(UpdateStatus, accountId, newStatus);
    const serviceProviders = yield call(fetchServiceProvidersApi);
    yield put(actions.fetchServiceProvidersSuccess(serviceProviders));
  } catch (error) {
    throw error;
  }
}

function* updateUser(action) {
  try {
    yield call(updateUserApi, action.payload);
    const serviceProviders = yield call(fetchServiceProvidersApi);
    yield put(actions.fetchServiceProvidersSuccess(serviceProviders));
  } catch (error) {
    throw error;
  }
}

export function* watchServiceProviders() {
  yield takeLatest(
    actionTypes.FETCH_SERVICE_PROVIDERS_REQUEST,
    fetchServiceProviders
  );
  yield takeLatest(
    actionTypes.REMOVE_SERVICE_PROVIDER_REQUEST,
    removeServiceProvider
  );
  yield takeLatest(actionTypes.ADD_SERVICE_PROVIDER_REQUEST, addServiceProvider);
  yield takeLatest(
    actionTypes.FETCH_SERVICE_PROVIDER_REQUESTS_REQUEST,
    fetchServiceProviderRequests
  );
  yield takeLatest(
    actionTypes.REJECT_SERVICE_PROVIDER_REQUEST,
    rejectServiceProvider
  );
  yield takeLatest(
    actionTypes.APPROVE_SERVICE_PROVIDER_REQUEST,
    approveServiceProvider
  );
  yield takeLatest(actionTypes.UPDATE_ACCOUNT_STATUS_REQUEST, updateAccountStatus);
  yield takeLatest(actionTypes.UPDATE_USER_DATA, updateUser);
}

export default watchServiceProviders;